import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Drawer, Input, Select, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getUsers } from '../../services/users';
import { updateNote, updateNoteSharing } from '../../services/notes';
import { Note, NotePermission, NoteVisibility } from '../../types';

interface Props {
  note?: Note;
  open: boolean;
  onClose: () => void;
}

const NoteAccessDrawer: React.FC<Props> = ({ note, open, onClose }) => {
  const queryClient = useQueryClient();
  const [members, setMembers] = useState<Array<{ userId: string; permission: Exclude<NotePermission, 'OWNER'> }>>([]);
  const [visibility, setVisibility] = useState<NoteVisibility>('PRIVATE');
  const [tags, setTags] = useState('');
  const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: getUsers, enabled: open });

  useEffect(() => {
    if (!open || !note) return;
    setMembers(note.members.map(member => ({ userId: member.userId, permission: member.permission })));
    setVisibility(note.visibility);
    setTags((note.tags || []).join(', '));
  }, [note, open]);

  const selectedUserIds = useMemo(() => new Set(members.map(member => member.userId)), [members]);
  const availableUsers = users.filter(user => user.id !== note?.ownerId);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!note) throw new Error('Note is unavailable');
      const nextTags = tags.split(',').map(tag => tag.trim()).filter(Boolean);
      if (visibility !== note.visibility || JSON.stringify(nextTags) !== JSON.stringify(note.tags || [])) {
        await updateNote(note.id, { visibility, tags: nextTags });
      }
      return updateNoteSharing(note.id, members);
    },
    onSuccess: updated => {
      queryClient.setQueryData(['note', updated.id], updated);
      queryClient.invalidateQueries({ queryKey: ['note'] });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      message.success('Access updated');
      onClose();
    },
    onError: (error: any) => message.error(error.response?.data?.error || 'Could not update access'),
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Details & access"
      width={440}
      placement="right"
      footer={<div className="library-access-drawer-footer"><Button onClick={onClose}>Cancel</Button><Button type="primary" loading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>Save changes</Button></div>}
    >
      <section className="library-access-section library-access-summary">
        <div><span className="library-access-label">Title</span><strong>{note?.title}</strong></div>
        <div><span className="library-access-label">Owner</span><strong>{note?.owner.name}</strong></div>
      </section>

      <section className="library-access-section">
        <span className="library-access-label">General access</span>
        <Select
          value={visibility}
          disabled={note?.currentPermission !== 'OWNER'}
          onChange={setVisibility}
          options={[
            { value: 'PRIVATE', label: 'Restricted · invited people only' },
            { value: 'ORGANIZATION', label: 'Everyone at Ourora can view' },
          ]}
          style={{ width: '100%' }}
        />
      </section>

      <section className="library-access-section">
        <span className="library-access-label">People with access</span>
        <p className="library-sharing-copy">Invite workspace members and choose whether they can view, edit, or manage this note.</p>
        <Select<string>
          showSearch
          placeholder="Add people…"
          optionFilterProp="label"
          value={undefined}
          onChange={(userId: string) => setMembers([...members, { userId, permission: 'VIEWER' }])}
          options={availableUsers.filter(user => !selectedUserIds.has(user.id)).map(user => ({ value: user.id, label: `${user.name} · ${user.email}` }))}
          style={{ width: '100%' }}
        />
        <div className="library-sharing-list">
          {members.map(member => {
            const user = availableUsers.find(candidate => candidate.id === member.userId) || note?.members.find(candidate => candidate.userId === member.userId)?.user;
            if (!user) return null;
            return (
              <div className="library-sharing-member" key={member.userId}>
                <Avatar src={user.photoUrl}>{user.name[0]}</Avatar>
                <div className="library-sharing-member-details"><strong>{user.name}</strong><span>{user.email}</span></div>
                <Select
                  value={member.permission}
                  onChange={permission => setMembers(members.map(current => current.userId === member.userId ? { ...current, permission } : current))}
                  options={[
                    { value: 'VIEWER', label: 'Can view' },
                    { value: 'EDITOR', label: 'Can edit' },
                    { value: 'MANAGER', label: 'Can manage' },
                  ]}
                />
                <Button aria-label={`Remove ${user.name}`} type="text" danger icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => setMembers(members.filter(current => current.userId !== member.userId))} />
              </div>
            );
          })}
        </div>
      </section>

      <section className="library-access-section">
        <span className="library-access-label">Tags</span>
        <Input value={tags} onChange={event => setTags(event.target.value)} placeholder="Comma-separated tags" />
      </section>
    </Drawer>
  );
};

export default NoteAccessDrawer;
